import React, { useEffect } from 'react';

const DeadLetterQueues = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Dead Letter Queues - Reliable Message Processing 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master Dead Letter Queues for robust message processing. Learn implementation patterns, error handling, and failure recovery strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master Dead Letter Queues for robust message processing. Learn implementation patterns, error handling, and failure recovery strategies.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Dead Letter Queues: Reliable Message Processing</h1>
        <p style={styles.subtitle}>
          Handle message failures gracefully and ensure reliable system operation
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Dead Letter Queues</h2>
          <p style={styles.paragraph}>
            Dead Letter Queues (DLQs) are a crucial component of robust message processing systems. 
            They provide a mechanism for handling messages that cannot be processed successfully, 
            either due to repeated failures, malformed data, or system errors. Instead of losing 
            these problematic messages or causing system failures, DLQs capture them for later 
            analysis, manual intervention, or alternative processing.
          </p>
          <p style={styles.paragraph}>
            DLQs are essential for building resilient distributed systems that can gracefully handle 
            failures, maintain system stability, and provide visibility into processing issues. They 
            act as a safety net that prevents message loss while allowing systems to continue operating 
            even when encountering problematic messages.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Core Concepts</h2>
          
          <h3 style={styles.heading}>What are Dead Letter Queues?</h3>
          <p style={styles.paragraph}>
            A Dead Letter Queue is a special queue that receives messages that cannot be delivered 
            to their intended destination or processed successfully. Messages typically end up in 
            a DLQ after exceeding retry limits, encountering permanent errors, or timing out.
          </p>
          <pre style={styles.codeBlock}>
{`Dead Letter Queue Concepts:

Message Processing Flow:
┌─────────────┐    ┌─────────────┐    ┌─────────────┐
│   Producer  │───▶│ Main Queue  │───▶│  Consumer   │
└─────────────┘    └─────────────┘    └─────────────┘
                           │                   │
                           │                   ▼
                           │            ┌─────────────┐
                           │            │ Processing  │
                           │            │   Fails     │
                           │            └─────────────┘
                           │                   │
                           │                   ▼
                           │            ┌─────────────┐
                           │            │ Retry Logic │
                           │            └─────────────┘
                           │                   │
                           │                   ▼
                           │            ┌─────────────┐
                           │            │Max Retries  │
                           │            │  Exceeded?  │
                           │            └─────────────┘
                           │                   │
                           │                   ▼
                           │            ┌─────────────┐
                           └───────────▶│Dead Letter  │
                                        │   Queue     │
                                        └─────────────┘

Key Characteristics:

1. Automatic Message Routing:
   - Messages automatically moved to DLQ after failure conditions
   - No manual intervention required for routing
   - Preserves original message content and metadata

2. Failure Conditions:
   - Maximum retry attempts exceeded
   - Message TTL (Time To Live) expired
   - Permanent processing errors
   - Consumer unavailability
   - Message format/validation errors

3. Message Preservation:
   - Original message content maintained
   - Processing history and error details added
   - Timestamps and retry counts preserved
   - Correlation IDs maintained

4. Visibility and Monitoring:
   - DLQ messages can be monitored and analyzed
   - Alerts can be set up for DLQ message arrival
   - Processing statistics and error patterns visible

Benefits:
✅ Prevents message loss
✅ Maintains system stability
✅ Enables error analysis
✅ Supports manual intervention
✅ Improves system observability
✅ Allows graceful degradation

Use Cases:
- Order processing systems
- Payment processing
- Email/notification systems
- Data pipeline processing
- API integration failures
- Batch job processing`}
          </pre>

          <h3 style={styles.heading}>Types of Dead Letter Queues</h3>
          <p style={styles.paragraph}>
            Different message brokers and systems implement DLQs with varying features and capabilities. 
            Understanding these differences helps in choosing the right approach for your system.
          </p>
          <pre style={styles.codeBlock}>
{`Types of Dead Letter Queues:

1. Simple Dead Letter Queue:
   - Single DLQ for all failed messages
   - Basic error information preserved
   - Manual processing required

Structure:
Main Queue → DLQ
- Simple routing
- All failures go to same place
- Basic error handling

2. Categorized Dead Letter Queues:
   - Different DLQs for different error types
   - Enables specialized handling
   - Better error classification

Structure:
Main Queue → Validation Error DLQ
           → Timeout Error DLQ
           → Processing Error DLQ
           → System Error DLQ

3. Hierarchical Dead Letter Queues:
   - Multiple levels of DLQs
   - Progressive error handling
   - Escalation paths

Structure:
Main Queue → Retry Queue → Secondary DLQ → Final DLQ

4. Time-based Dead Letter Queues:
   - Messages moved based on age
   - Automatic cleanup
   - Time-based processing

Structure:
Main Queue → Recent Failures DLQ → Old Failures DLQ

Message Broker Implementations:

Amazon SQS DLQ:
- Built-in DLQ support
- Configurable retry counts
- Message redrive capability
- CloudWatch integration

Apache Kafka DLQ:
- Topic-based DLQ pattern
- Custom error handling
- Offset management
- Stream processing integration

RabbitMQ DLQ:
- Exchange-based routing
- TTL and retry support
- Plugin ecosystem
- Management UI

Azure Service Bus DLQ:
- Automatic DLQ creation
- Message sessions support
- Duplicate detection
- Enterprise features

Redis Streams DLQ:
- Stream-based approach
- Consumer group support
- Pending entries list
- Custom implementation needed`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Patterns</h2>
          
          <h3 style={styles.heading}>Basic Dead Letter Queue Implementation</h3>
          <p style={styles.paragraph}>
            Implementing a DLQ system requires careful consideration of retry logic, error classification, 
            and message routing. Here's a comprehensive implementation using different message brokers.
          </p>
          <pre style={styles.codeBlock}>
{`Dead Letter Queue Implementation:

# Python implementation with Redis
import redis
import json
import time
import logging
from typing import Dict, Any, Optional, Callable
from dataclasses import dataclass
from enum import Enum

class MessageStatus(Enum):
    PENDING = "pending"
    PROCESSING = "processing"
    COMPLETED = "completed"
    FAILED = "failed"
    DEAD_LETTER = "dead_letter"

@dataclass
class Message:
    id: str
    content: Dict[Any, Any]
    retry_count: int = 0
    max_retries: int = 3
    created_at: float = None
    last_attempt_at: float = None
    error_history: list = None
    
    def __post_init__(self):
        if self.created_at is None:
            self.created_at = time.time()
        if self.error_history is None:
            self.error_history = []

class DeadLetterQueueManager:
    def __init__(self, redis_client: redis.Redis, queue_name: str):
        self.redis = redis_client
        self.queue_name = queue_name
        self.dlq_name = f"{queue_name}_dlq"
        self.processing_set = f"{queue_name}_processing"
        self.retry_queue = f"{queue_name}_retry"
        self.logger = logging.getLogger(__name__)
    
    def send_message(self, message_content: Dict[Any, Any], 
                    message_id: str = None, max_retries: int = 3) -> str:
        """Send message to queue"""
        if message_id is None:
            message_id = f"msg_{int(time.time() * 1000)}_{hash(str(message_content)) % 10000}"
        
        message = Message(
            id=message_id,
            content=message_content,
            max_retries=max_retries
        )
        
        # Add to queue
        self.redis.lpush(self.queue_name, json.dumps(message.__dict__))
        self.logger.info(f"Message {message_id} sent to queue")
        
        return message_id
    
    def process_messages(self, processor_func: Callable, timeout: int = 10):
        """Process messages with DLQ support"""
        while True:
            try:
                # Get message from queue (blocking)
                result = self.redis.brpop(self.queue_name, timeout)
                if not result:
                    continue
                
                queue_name, message_data = result
                message_dict = json.loads(message_data)
                message = Message(**message_dict)
                
                # Mark as processing
                self._mark_processing(message)
                
                try:
                    # Process message
                    self.logger.info(f"Processing message {message.id}")
                    processor_func(message.content)
                    
                    # Mark as completed
                    self._mark_completed(message)
                    self.logger.info(f"Message {message.id} processed successfully")
                    
                except Exception as e:
                    # Handle processing failure
                    self._handle_processing_failure(message, e)
                    
            except KeyboardInterrupt:
                self.logger.info("Shutting down message processor")
                break
            except Exception as e:
                self.logger.error(f"Unexpected error in message processing: {e}")
    
    def _mark_processing(self, message: Message):
        """Mark message as being processed"""
        message.last_attempt_at = time.time()
        self.redis.sadd(self.processing_set, json.dumps(message.__dict__))
    
    def _mark_completed(self, message: Message):
        """Mark message as completed"""
        self.redis.srem(self.processing_set, json.dumps(message.__dict__))
    
    def _handle_processing_failure(self, message: Message, error: Exception):
        """Handle message processing failure"""
        message.retry_count += 1
        message.error_history.append({
            'error': str(error),
            'timestamp': time.time(),
            'attempt': message.retry_count
        })
        
        # Remove from processing set
        self.redis.srem(self.processing_set, json.dumps(message.__dict__))
        
        if message.retry_count < message.max_retries:
            # Retry with exponential backoff
            delay = min(2 ** message.retry_count, 300)  # Max 5 minutes
            self._schedule_retry(message, delay)
            self.logger.warning(
                f"Message {message.id} failed (attempt {message.retry_count}), "
                f"retrying in {delay} seconds"
            )
        else:
            # Send to dead letter queue
            self._send_to_dlq(message, error)
            self.logger.error(
                f"Message {message.id} exceeded max retries, sent to DLQ"
            )
    
    def _schedule_retry(self, message: Message, delay: int):
        """Schedule message for retry"""
        retry_time = time.time() + delay
        retry_data = {
            'message': message.__dict__,
            'retry_time': retry_time
        }
        
        # Add to retry queue with score as retry time
        self.redis.zadd(self.retry_queue, {json.dumps(retry_data): retry_time})
    
    def _send_to_dlq(self, message: Message, error: Exception):
        """Send message to dead letter queue"""
        dlq_entry = {
            'original_message': message.__dict__,
            'final_error': str(error),
            'dlq_timestamp': time.time(),
            'total_attempts': message.retry_count,
            'processing_duration': time.time() - message.created_at
        }
        
        self.redis.lpush(self.dlq_name, json.dumps(dlq_entry))
        
        # Update metrics
        self._update_dlq_metrics(message, error)
    
    def process_retry_queue(self):
        """Process scheduled retries"""
        current_time = time.time()
        
        # Get messages ready for retry
        retry_messages = self.redis.zrangebyscore(
            self.retry_queue, 0, current_time, withscores=True
        )
        
        for retry_data, score in retry_messages:
            try:
                retry_info = json.loads(retry_data)
                message_dict = retry_info['message']
                
                # Add back to main queue
                self.redis.lpush(self.queue_name, json.dumps(message_dict))
                
                # Remove from retry queue
                self.redis.zrem(self.retry_queue, retry_data)
                
                self.logger.info(f"Message {message_dict['id']} moved from retry queue to main queue")
                
            except Exception as e:
                self.logger.error(f"Error processing retry queue: {e}")
    
    def get_dlq_messages(self, limit: int = 100) -> list:
        """Get messages from dead letter queue"""
        messages = self.redis.lrange(self.dlq_name, 0, limit - 1)
        return [json.loads(msg) for msg in messages]
    
    def reprocess_dlq_message(self, dlq_index: int) -> bool:
        """Reprocess a message from DLQ"""
        try:
            # Get message from DLQ
            dlq_entry_data = self.redis.lindex(self.dlq_name, dlq_index)
            if not dlq_entry_data:
                return False
            
            dlq_entry = json.loads(dlq_entry_data)
            original_message = dlq_entry['original_message']
            
            # Reset retry count and error history
            original_message['retry_count'] = 0
            original_message['error_history'] = []
            
            # Add back to main queue
            self.redis.lpush(self.queue_name, json.dumps(original_message))
            
            # Remove from DLQ
            self.redis.lrem(self.dlq_name, 1, dlq_entry_data)
            
            self.logger.info(f"Message {original_message['id']} reprocessed from DLQ")
            return True
            
        except Exception as e:
            self.logger.error(f"Error reprocessing DLQ message: {e}")
            return False
    
    def _update_dlq_metrics(self, message: Message, error: Exception):
        """Update DLQ metrics for monitoring"""
        metrics_key = f"{self.queue_name}_metrics"
        
        # Increment counters
        self.redis.hincrby(metrics_key, "dlq_total", 1)
        self.redis.hincrby(metrics_key, f"dlq_error_{type(error).__name__}", 1)
        
        # Update timestamps
        self.redis.hset(metrics_key, "last_dlq_message", time.time())
    
    def get_queue_stats(self) -> Dict[str, Any]:
        """Get queue statistics"""
        stats = {
            'main_queue_size': self.redis.llen(self.queue_name),
            'dlq_size': self.redis.llen(self.dlq_name),
            'processing_count': self.redis.scard(self.processing_set),
            'retry_queue_size': self.redis.zcard(self.retry_queue)
        }
        
        # Get metrics
        metrics_key = f"{self.queue_name}_metrics"
        metrics = self.redis.hgetall(metrics_key)
        stats['metrics'] = {k.decode(): v.decode() for k, v in metrics.items()}
        
        return stats

# Usage Example
def message_processor(content):
    """Example message processor"""
    if content.get('should_fail'):
        raise ValueError("Simulated processing failure")
    
    # Simulate processing time
    time.sleep(0.1)
    print(f"Processed: {content}")

# Setup
redis_client = redis.Redis(host='localhost', port=6379, db=0)
dlq_manager = DeadLetterQueueManager(redis_client, 'orders')

# Send messages
dlq_manager.send_message({'order_id': '123', 'amount': 100})
dlq_manager.send_message({'order_id': '124', 'should_fail': True})  # Will fail
dlq_manager.send_message({'order_id': '125', 'amount': 200})

# Process messages (run in separate thread/process)
import threading

def run_processor():
    dlq_manager.process_messages(message_processor)

def run_retry_processor():
    while True:
        dlq_manager.process_retry_queue()
        time.sleep(5)

# Start processors
processor_thread = threading.Thread(target=run_processor, daemon=True)
retry_thread = threading.Thread(target=run_retry_processor, daemon=True)

processor_thread.start()
retry_thread.start()

# Monitor queues
stats = dlq_manager.get_queue_stats()
print("Queue Stats:", stats)

# Check DLQ messages
dlq_messages = dlq_manager.get_dlq_messages()
print("DLQ Messages:", dlq_messages)

# Reprocess a DLQ message
if dlq_messages:
    dlq_manager.reprocess_dlq_message(0)`}
          </pre>

          <h3 style={styles.heading}>Advanced DLQ Patterns</h3>
          <p style={styles.paragraph}>
            Advanced DLQ implementations include features like message classification, automated 
            recovery, poison message detection, and integration with monitoring systems.
          </p>
          <pre style={styles.codeBlock}>
{`Advanced DLQ Patterns:

# Categorized Dead Letter Queue System
class ErrorCategory(Enum):
    VALIDATION_ERROR = "validation_error"
    TIMEOUT_ERROR = "timeout_error"
    SYSTEM_ERROR = "system_error"
    BUSINESS_LOGIC_ERROR = "business_logic_error"
    TRANSIENT_ERROR = "transient_error"

class CategorizedDLQManager:
    def __init__(self, redis_client: redis.Redis, queue_name: str):
        self.redis = redis_client
        self.queue_name = queue_name
        self.dlq_categories = {
            ErrorCategory.VALIDATION_ERROR: f"{queue_name}_dlq_validation",
            ErrorCategory.TIMEOUT_ERROR: f"{queue_name}_dlq_timeout",
            ErrorCategory.SYSTEM_ERROR: f"{queue_name}_dlq_system",
            ErrorCategory.BUSINESS_LOGIC_ERROR: f"{queue_name}_dlq_business",
            ErrorCategory.TRANSIENT_ERROR: f"{queue_name}_dlq_transient"
        }
        self.logger = logging.getLogger(__name__)
    
    def classify_error(self, error: Exception) -> ErrorCategory:
        """Classify error into appropriate category"""
        error_type = type(error).__name__
        error_message = str(error).lower()
        
        if isinstance(error, (ValueError, TypeError)) or 'validation' in error_message:
            return ErrorCategory.VALIDATION_ERROR
        elif isinstance(error, TimeoutError) or 'timeout' in error_message:
            return ErrorCategory.TIMEOUT_ERROR
        elif 'connection' in error_message or 'network' in error_message:
            return ErrorCategory.TRANSIENT_ERROR
        elif 'business' in error_message or 'rule' in error_message:
            return ErrorCategory.BUSINESS_LOGIC_ERROR
        else:
            return ErrorCategory.SYSTEM_ERROR
    
    def send_to_categorized_dlq(self, message: Message, error: Exception):
        """Send message to appropriate DLQ based on error category"""
        category = self.classify_error(error)
        dlq_name = self.dlq_categories[category]
        
        dlq_entry = {
            'original_message': message.__dict__,
            'error_category': category.value,
            'error_type': type(error).__name__,
            'error_message': str(error),
            'dlq_timestamp': time.time(),
            'total_attempts': message.retry_count,
            'processing_duration': time.time() - message.created_at
        }
        
        self.redis.lpush(dlq_name, json.dumps(dlq_entry))
        self.logger.error(f"Message {message.id} sent to {category.value} DLQ")
        
        # Update category-specific metrics
        self._update_category_metrics(category, error)
    
    def get_dlq_summary(self) -> Dict[str, int]:
        """Get summary of messages in each DLQ category"""
        summary = {}
        for category, dlq_name in self.dlq_categories.items():
            summary[category.value] = self.redis.llen(dlq_name)
        return summary
    
    def auto_recover_transient_errors(self, max_age_hours: int = 1):
        """Automatically retry messages from transient error DLQ"""
        dlq_name = self.dlq_categories[ErrorCategory.TRANSIENT_ERROR]
        current_time = time.time()
        max_age_seconds = max_age_hours * 3600
        
        # Get all messages from transient DLQ
        messages = self.redis.lrange(dlq_name, 0, -1)
        recovered_count = 0
        
        for msg_data in messages:
            try:
                dlq_entry = json.loads(msg_data)
                dlq_timestamp = dlq_entry['dlq_timestamp']
                
                # Check if message is old enough to retry
                if current_time - dlq_timestamp > max_age_seconds:
                    original_message = dlq_entry['original_message']
                    
                    # Reset retry count for transient errors
                    original_message['retry_count'] = 0
                    original_message['error_history'] = []
                    
                    # Add back to main queue
                    self.redis.lpush(self.queue_name, json.dumps(original_message))
                    
                    # Remove from DLQ
                    self.redis.lrem(dlq_name, 1, msg_data)
                    
                    recovered_count += 1
                    self.logger.info(f"Auto-recovered message {original_message['id']} from transient DLQ")
                    
            except Exception as e:
                self.logger.error(f"Error in auto-recovery: {e}")
        
        return recovered_count

# Poison Message Detection
class PoisonMessageDetector:
    def __init__(self, redis_client: redis.Redis, threshold: int = 5):
        self.redis = redis_client
        self.threshold = threshold
        self.poison_key = "poison_messages"
        self.logger = logging.getLogger(__name__)
    
    def track_message_failure(self, message_id: str, error: Exception):
        """Track message failures to detect poison messages"""
        failure_key = f"failures:{message_id}"
        
        # Increment failure count
        failure_count = self.redis.incr(failure_key)
        self.redis.expire(failure_key, 3600)  # Expire after 1 hour
        
        # Record failure details
        failure_data = {
            'timestamp': time.time(),
            'error_type': type(error).__name__,
            'error_message': str(error)
        }
        
        self.redis.lpush(f"failure_history:{message_id}", json.dumps(failure_data))
        self.redis.expire(f"failure_history:{message_id}", 3600)
        
        # Check if message is poison
        if failure_count >= self.threshold:
            self._mark_as_poison(message_id)
            return True
        
        return False
    
    def _mark_as_poison(self, message_id: str):
        """Mark message as poison"""
        poison_data = {
            'message_id': message_id,
            'detected_at': time.time(),
            'failure_count': self.redis.get(f"failures:{message_id}").decode()
        }
        
        self.redis.sadd(self.poison_key, json.dumps(poison_data))
        self.logger.warning(f"Message {message_id} marked as poison")
    
    def is_poison_message(self, message_id: str) -> bool:
        """Check if message is marked as poison"""
        poison_messages = self.redis.smembers(self.poison_key)
        for poison_data in poison_messages:
            poison_info = json.loads(poison_data)
            if poison_info['message_id'] == message_id:
                return True
        return False
    
    def get_poison_messages(self) -> list:
        """Get all poison messages"""
        poison_messages = self.redis.smembers(self.poison_key)
        return [json.loads(msg) for msg in poison_messages]

# DLQ Monitoring and Alerting
class DLQMonitor:
    def __init__(self, dlq_manager: DeadLetterQueueManager):
        self.dlq_manager = dlq_manager
        self.logger = logging.getLogger(__name__)
        self.alert_thresholds = {
            'dlq_size': 10,
            'dlq_rate': 5,  # messages per minute
            'processing_time': 300  # seconds
        }
    
    def check_dlq_health(self) -> Dict[str, Any]:
        """Check DLQ health and generate alerts"""
        stats = self.dlq_manager.get_queue_stats()
        alerts = []
        
        # Check DLQ size
        dlq_size = stats['dlq_size']
        if dlq_size > self.alert_thresholds['dlq_size']:
            alerts.append({
                'type': 'HIGH_DLQ_SIZE',
                'message': f"DLQ size ({dlq_size}) exceeds threshold ({self.alert_thresholds['dlq_size']})",
                'severity': 'WARNING' if dlq_size < self.alert_thresholds['dlq_size'] * 2 else 'CRITICAL'
            })
        
        # Check processing queue backup
        processing_count = stats['processing_count']
        if processing_count > 50:
            alerts.append({
                'type': 'HIGH_PROCESSING_COUNT',
                'message': f"High number of messages in processing ({processing_count})",
                'severity': 'WARNING'
            })
        
        # Check retry queue size
        retry_size = stats['retry_queue_size']
        if retry_size > 20:
            alerts.append({
                'type': 'HIGH_RETRY_QUEUE',
                'message': f"High number of messages in retry queue ({retry_size})",
                'severity': 'INFO'
            })
        
        return {
            'status': 'HEALTHY' if not alerts else 'UNHEALTHY',
            'stats': stats,
            'alerts': alerts,
            'timestamp': time.time()
        }
    
    def generate_dlq_report(self) -> Dict[str, Any]:
        """Generate comprehensive DLQ report"""
        dlq_messages = self.dlq_manager.get_dlq_messages()
        
        # Analyze error patterns
        error_types = {}
        error_messages = {}
        hourly_distribution = {}
        
        for msg in dlq_messages:
            # Count error types
            error_type = msg.get('final_error', 'Unknown')
            error_types[error_type] = error_types.get(error_type, 0) + 1
            
            # Analyze error messages
            error_msg = str(msg.get('final_error', ''))[:100]
            error_messages[error_msg] = error_messages.get(error_msg, 0) + 1
            
            # Hourly distribution
            timestamp = msg.get('dlq_timestamp', time.time())
            hour = int(timestamp // 3600)
            hourly_distribution[hour] = hourly_distribution.get(hour, 0) + 1
        
        return {
            'total_dlq_messages': len(dlq_messages),
            'error_type_distribution': error_types,
            'top_error_messages': sorted(error_messages.items(), key=lambda x: x[1], reverse=True)[:10],
            'hourly_distribution': hourly_distribution,
            'generated_at': time.time()
        }

# Usage Example with Advanced Features
categorized_dlq = CategorizedDLQManager(redis_client, 'orders')
poison_detector = PoisonMessageDetector(redis_client)
monitor = DLQMonitor(dlq_manager)

# Enhanced message processor with poison detection
def enhanced_processor(content):
    message_id = content.get('id', 'unknown')
    
    try:
        # Simulate processing
        if content.get('should_fail'):
            raise ValueError("Simulated failure")
        
        print(f"Processed: {content}")
        
    except Exception as e:
        # Check for poison message
        if poison_detector.track_message_failure(message_id, e):
            # Handle poison message specially
            print(f"Poison message detected: {message_id}")
            # Could send to special quarantine queue
        
        raise e

# Monitor DLQ health
health_report = monitor.check_dlq_health()
print("DLQ Health:", health_report)

# Generate DLQ analysis report
dlq_report = monitor.generate_dlq_report()
print("DLQ Report:", dlq_report)

# Auto-recover transient errors
recovered = categorized_dlq.auto_recover_transient_errors()
print(f"Auto-recovered {recovered} messages from transient errors")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Message Broker Integrations</h2>
          
          <h3 style={styles.heading}>Amazon SQS Dead Letter Queues</h3>
          <p style={styles.paragraph}>
            Amazon SQS provides built-in Dead Letter Queue functionality with configurable retry 
            policies and seamless integration with other AWS services.
          </p>
          <pre style={styles.codeBlock}>
{`Amazon SQS DLQ Implementation:

import boto3
import json
from botocore.exceptions import ClientError

class SQSDLQManager:
    def __init__(self, region_name='us-east-1'):
        self.sqs = boto3.client('sqs', region_name=region_name)
        self.region = region_name
    
    def create_queue_with_dlq(self, queue_name: str, dlq_name: str, 
                             max_receive_count: int = 3) -> tuple:
        """Create main queue with associated DLQ"""
        try:
            # Create DLQ first
            dlq_response = self.sqs.create_queue(
                QueueName=dlq_name,
                Attributes={
                    'MessageRetentionPeriod': '1209600',  # 14 days
                    'VisibilityTimeoutSeconds': '60'
                }
            )
            dlq_url = dlq_response['QueueUrl']
            
            # Get DLQ ARN
            dlq_attributes = self.sqs.get_queue_attributes(
                QueueUrl=dlq_url,
                AttributeNames=['QueueArn']
            )
            dlq_arn = dlq_attributes['Attributes']['QueueArn']
            
            # Create main queue with DLQ policy
            redrive_policy = {
                'deadLetterTargetArn': dlq_arn,
                'maxReceiveCount': max_receive_count
            }
            
            main_response = self.sqs.create_queue(
                QueueName=queue_name,
                Attributes={
                    'MessageRetentionPeriod': '1209600',
                    'VisibilityTimeoutSeconds': '30',
                    'RedrivePolicy': json.dumps(redrive_policy)
                }
            )
            main_url = main_response['QueueUrl']
            
            return main_url, dlq_url
            
        except ClientError as e:
            print(f"Error creating queues: {e}")
            raise
    
    def send_message(self, queue_url: str, message_body: dict, 
                    delay_seconds: int = 0) -> str:
        """Send message to SQS queue"""
        try:
            response = self.sqs.send_message(
                QueueUrl=queue_url,
                MessageBody=json.dumps(message_body),
                DelaySeconds=delay_seconds,
                MessageAttributes={
                    'Timestamp': {
                        'StringValue': str(int(time.time())),
                        'DataType': 'Number'
                    }
                }
            )
            return response['MessageId']
            
        except ClientError as e:
            print(f"Error sending message: {e}")
            raise
    
    def process_messages(self, queue_url: str, processor_func, 
                        max_messages: int = 10, wait_time: int = 20):
        """Process messages with automatic DLQ handling"""
        while True:
            try:
                # Receive messages
                response = self.sqs.receive_message(
                    QueueUrl=queue_url,
                    MaxNumberOfMessages=max_messages,
                    WaitTimeSeconds=wait_time,
                    MessageAttributeNames=['All']
                )
                
                messages = response.get('Messages', [])
                if not messages:
                    continue
                
                for message in messages:
                    try:
                        # Process message
                        message_body = json.loads(message['Body'])
                        processor_func(message_body)
                        
                        # Delete message on success
                        self.sqs.delete_message(
                            QueueUrl=queue_url,
                            ReceiptHandle=message['ReceiptHandle']
                        )
                        
                        print(f"Processed message: {message['MessageId']}")
                        
                    except Exception as e:
                        print(f"Error processing message {message['MessageId']}: {e}")
                        # Message will be retried automatically
                        # After max retries, SQS will move it to DLQ
                        
            except KeyboardInterrupt:
                print("Shutting down message processor")
                break
            except Exception as e:
                print(f"Error in message processing loop: {e}")
                time.sleep(5)
    
    def get_dlq_messages(self, dlq_url: str, max_messages: int = 10) -> list:
        """Get messages from DLQ for analysis"""
        try:
            response = self.sqs.receive_message(
                QueueUrl=dlq_url,
                MaxNumberOfMessages=max_messages,
                MessageAttributeNames=['All']
            )
            
            return response.get('Messages', [])
            
        except ClientError as e:
            print(f"Error getting DLQ messages: {e}")
            return []
    
    def redrive_dlq_messages(self, dlq_url: str, main_queue_url: str, 
                           max_messages: int = 10) -> int:
        """Move messages from DLQ back to main queue"""
        moved_count = 0
        
        while True:
            messages = self.get_dlq_messages(dlq_url, max_messages)
            if not messages:
                break
            
            for message in messages:
                try:
                    # Send message back to main queue
                    self.sqs.send_message(
                        QueueUrl=main_queue_url,
                        MessageBody=message['Body'],
                        MessageAttributes=message.get('MessageAttributes', {})
                    )
                    
                    # Delete from DLQ
                    self.sqs.delete_message(
                        QueueUrl=dlq_url,
                        ReceiptHandle=message['ReceiptHandle']
                    )
                    
                    moved_count += 1
                    print(f"Moved message {message['MessageId']} from DLQ to main queue")
                    
                except Exception as e:
                    print(f"Error moving message: {e}")
        
        return moved_count
    
    def get_queue_attributes(self, queue_url: str) -> dict:
        """Get queue attributes including DLQ stats"""
        try:
            response = self.sqs.get_queue_attributes(
                QueueUrl=queue_url,
                AttributeNames=['All']
            )
            return response['Attributes']
            
        except ClientError as e:
            print(f"Error getting queue attributes: {e}")
            return {}

# Usage Example
sqs_dlq = SQSDLQManager()

# Create queues
main_queue_url, dlq_url = sqs_dlq.create_queue_with_dlq(
    'order-processing',
    'order-processing-dlq',
    max_receive_count=3
)

# Send test messages
sqs_dlq.send_message(main_queue_url, {'order_id': '123', 'amount': 100})
sqs_dlq.send_message(main_queue_url, {'order_id': '124', 'should_fail': True})

# Process messages (messages that fail 3 times go to DLQ automatically)
def process_order(message_body):
    if message_body.get('should_fail'):
        raise ValueError("Order processing failed")
    print(f"Processed order: {message_body}")

# Run processor
sqs_dlq.process_messages(main_queue_url, process_order)

# Check DLQ messages
dlq_messages = sqs_dlq.get_dlq_messages(dlq_url)
print(f"Found {len(dlq_messages)} messages in DLQ")

# Redrive DLQ messages back to main queue
moved_count = sqs_dlq.redrive_dlq_messages(dlq_url, main_queue_url)
print(f"Moved {moved_count} messages from DLQ")`}
          </pre>

          <h3 style={styles.heading}>Apache Kafka Dead Letter Topics</h3>
          <p style={styles.paragraph}>
            Kafka implements DLQ functionality using dead letter topics, providing powerful stream 
            processing capabilities and detailed error tracking.
          </p>
          <pre style={styles.codeBlock}>
{`Kafka Dead Letter Topic Implementation:

from kafka import KafkaProducer, KafkaConsumer
from kafka.errors import KafkaError
import json
import time
import logging
from typing import Dict, Any

class KafkaDLTManager:
    def __init__(self, bootstrap_servers: str = 'localhost:9092'):
        self.bootstrap_servers = bootstrap_servers
        self.producer = KafkaProducer(
            bootstrap_servers=[bootstrap_servers],
            value_serializer=lambda v: json.dumps(v).encode('utf-8'),
            key_serializer=lambda k: k.encode('utf-8') if k else None
        )
        self.logger = logging.getLogger(__name__)
    
    def create_consumer(self, topic: str, group_id: str) -> KafkaConsumer:
        """Create Kafka consumer"""
        return KafkaConsumer(
            topic,
            bootstrap_servers=[self.bootstrap_servers],
            group_id=group_id,
            value_deserializer=lambda m: json.loads(m.decode('utf-8')),
            key_deserializer=lambda k: k.decode('utf-8') if k else None,
            enable_auto_commit=False,  # Manual commit for error handling
            auto_offset_reset='earliest'
        )
    
    def send_message(self, topic: str, message: Dict[Any, Any], 
                    key: str = None) -> bool:
        """Send message to Kafka topic"""
        try:
            # Add metadata
            enriched_message = {
                **message,
                'timestamp': time.time(),
                'attempts': 0
            }
            
            future = self.producer.send(topic, value=enriched_message, key=key)
            record_metadata = future.get(timeout=10)
            
            self.logger.info(f"Message sent to {record_metadata.topic}:{record_metadata.partition}:{record_metadata.offset}")
            return True
            
        except KafkaError as e:
            self.logger.error(f"Failed to send message: {e}")
            return False
    
    def process_messages_with_dlt(self, main_topic: str, dlt_topic: str, 
                                 group_id: str, processor_func, 
                                 max_retries: int = 3):
        """Process messages with DLT support"""
        consumer = self.create_consumer(main_topic, group_id)
        
        try:
            for message in consumer:
                try:
                    # Extract message data
                    message_data = message.value
                    attempts = message_data.get('attempts', 0)
                    
                    self.logger.info(f"Processing message (attempt {attempts + 1}): {message.key}")
                    
                    # Process message
                    processor_func(message_data)
                    
                    # Commit on success
                    consumer.commit()
                    self.logger.info(f"Successfully processed message: {message.key}")
                    
                except Exception as e:
                    self.logger.error(f"Error processing message {message.key}: {e}")
                    
                    # Increment attempt count
                    attempts += 1
                    message_data['attempts'] = attempts
                    message_data['last_error'] = str(e)
                    message_data['last_error_timestamp'] = time.time()
                    
                    if attempts < max_retries:
                        # Retry: send back to main topic with delay
                        retry_topic = f"{main_topic}.retry"
                        self.send_message(retry_topic, message_data, message.key)
                        self.logger.info(f"Message {message.key} sent to retry topic (attempt {attempts})")
                    else:
                        # Send to DLT
                        self.send_to_dlt(dlt_topic, message_data, message.key, e)
                        self.logger.error(f"Message {message.key} sent to DLT after {attempts} attempts")
                    
                    # Commit the failed message
                    consumer.commit()
                    
        except KeyboardInterrupt:
            self.logger.info("Shutting down consumer")
        finally:
            consumer.close()
    
    def send_to_dlt(self, dlt_topic: str, original_message: Dict[Any, Any], 
                   message_key: str, error: Exception):
        """Send message to Dead Letter Topic"""
        dlt_message = {
            'original_message': original_message,
            'error_type': type(error).__name__,
            'error_message': str(error),
            'dlt_timestamp': time.time(),
            'total_attempts': original_message.get('attempts', 0),
            'original_topic': original_message.get('original_topic', 'unknown')
        }
        
        self.send_message(dlt_topic, dlt_message, message_key)
    
    def process_dlt_messages(self, dlt_topic: str, group_id: str, 
                           analysis_func=None):
        """Process DLT messages for analysis"""
        consumer = self.create_consumer(dlt_topic, group_id)
        
        try:
            for message in consumer:
                dlt_data = message.value
                
                if analysis_func:
                    analysis_func(dlt_data)
                else:
                    # Default analysis
                    self._analyze_dlt_message(dlt_data)
                
                consumer.commit()
                
        except KeyboardInterrupt:
            self.logger.info("Shutting down DLT processor")
        finally:
            consumer.close()
    
    def _analyze_dlt_message(self, dlt_data: Dict[Any, Any]):
        """Default DLT message analysis"""
        original_msg = dlt_data['original_message']
        error_type = dlt_data['error_type']
        total_attempts = dlt_data['total_attempts']
        
        print(f"DLT Analysis:")
        print(f"  Message Key: {original_msg.get('key', 'N/A')}")
        print(f"  Error Type: {error_type}")
        print(f"  Total Attempts: {total_attempts}")
        print(f"  Error Message: {dlt_data['error_message']}")
        print(f"  DLT Timestamp: {dlt_data['dlt_timestamp']}")
        print("---")
    
    def replay_dlt_messages(self, dlt_topic: str, target_topic: str, 
                           group_id: str, filter_func=None) -> int:
        """Replay messages from DLT to target topic"""
        consumer = self.create_consumer(dlt_topic, group_id)
        replayed_count = 0
        
        try:
            for message in consumer:
                dlt_data = message.value
                original_message = dlt_data['original_message']
                
                # Apply filter if provided
                if filter_func and not filter_func(dlt_data):
                    consumer.commit()
                    continue
                
                # Reset attempt count
                original_message['attempts'] = 0
                original_message['replayed_from_dlt'] = True
                original_message['replay_timestamp'] = time.time()
                
                # Send to target topic
                if self.send_message(target_topic, original_message, message.key):
                    replayed_count += 1
                    self.logger.info(f"Replayed message {message.key} from DLT to {target_topic}")
                
                consumer.commit()
                
        except KeyboardInterrupt:
            self.logger.info("Shutting down DLT replay")
        finally:
            consumer.close()
        
        return replayed_count

# Kafka Streams DLT Integration
from kafka.admin import KafkaAdminClient, NewTopic

class KafkaStreamsDLT:
    def __init__(self, bootstrap_servers: str = 'localhost:9092'):
        self.bootstrap_servers = bootstrap_servers
        self.admin_client = KafkaAdminClient(
            bootstrap_servers=[bootstrap_servers]
        )
    
    def create_topics_with_dlt(self, base_topic: str, partitions: int = 3, 
                              replication_factor: int = 1):
        """Create main topic with associated DLT and retry topics"""
        topics = [
            NewTopic(name=base_topic, num_partitions=partitions, 
                    replication_factor=replication_factor),
            NewTopic(name=f"{base_topic}.retry", num_partitions=partitions, 
                    replication_factor=replication_factor),
            NewTopic(name=f"{base_topic}.dlt", num_partitions=partitions, 
                    replication_factor=replication_factor)
        ]
        
        try:
            self.admin_client.create_topics(topics)
            print(f"Created topics for {base_topic}")
        except Exception as e:
            print(f"Error creating topics: {e}")

# Usage Example
kafka_dlt = KafkaDLTManager()

# Create topics
streams_dlt = KafkaStreamsDLT()
streams_dlt.create_topics_with_dlt('orders', partitions=3)

# Send test messages
kafka_dlt.send_message('orders', {'order_id': '123', 'amount': 100}, key='order-123')
kafka_dlt.send_message('orders', {'order_id': '124', 'should_fail': True}, key='order-124')

# Message processor
def process_order(message_data):
    if message_data.get('should_fail'):
        raise ValueError("Order processing failed")
    print(f"Processed order: {message_data['order_id']}")

# Process messages with DLT
import threading

def run_processor():
    kafka_dlt.process_messages_with_dlt(
        'orders', 'orders.dlt', 'order-processor', process_order
    )

# Start processor in background
processor_thread = threading.Thread(target=run_processor, daemon=True)
processor_thread.start()

# Process DLT messages for analysis
def analyze_failed_order(dlt_data):
    original_msg = dlt_data['original_message']
    print(f"Failed order analysis: {original_msg['order_id']} - {dlt_data['error_message']}")

# Analyze DLT messages
kafka_dlt.process_dlt_messages('orders.dlt', 'dlt-analyzer', analyze_failed_order)

# Replay DLT messages (with filter)
def should_replay(dlt_data):
    # Only replay messages that failed due to temporary issues
    return 'temporary' in dlt_data['error_message'].lower()

replayed = kafka_dlt.replay_dlt_messages('orders.dlt', 'orders', 'dlt-replay', should_replay)
print(f"Replayed {replayed} messages from DLT")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Design for failure from the start:</strong> Implement DLQs as part of your 
              initial system design, not as an afterthought when problems arise.
            </li>
            <li style={styles.listItem}>
              <strong>Classify and categorize errors:</strong> Use different DLQs for different 
              types of failures to enable appropriate handling and recovery strategies.
            </li>
            <li style={styles.listItem}>
              <strong>Implement comprehensive monitoring:</strong> Set up alerts for DLQ message 
              arrivals and track error patterns to identify systemic issues.
            </li>
            <li style={styles.listItem}>
              <strong>Automate recovery where possible:</strong> Implement automatic retry mechanisms 
              for transient errors and poison message detection for permanent failures.
            </li>
            <li style={styles.listItem}>
              <strong>Preserve message context:</strong> Include sufficient metadata in DLQ messages 
              to enable effective debugging and manual intervention.
            </li>
            <li style={styles.listItem}>
              <strong>Regular DLQ maintenance:</strong> Implement processes to regularly review, 
              analyze, and clean up DLQ messages to prevent unbounded growth.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Dead Letter Queues are essential for building robust, fault-tolerant message processing 
            systems. They provide a safety net that prevents message loss while maintaining system 
            stability in the face of processing failures. Proper DLQ implementation includes retry 
            logic, error classification, monitoring, and recovery mechanisms.
          </p>
          <p style={styles.paragraph}>
            The key to successful DLQ implementation is balancing automatic recovery with manual 
            intervention capabilities, ensuring that transient issues are handled automatically while 
            providing tools for analyzing and resolving persistent problems. This approach enables 
            systems to be both resilient and observable.
          </p>
        </section>
      </main>
    </article>
  );
};

export default DeadLetterQueues;
